import { Button, Job } from '@cibli/shared';
import { ArrowLeftIcon, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseTypeEnum } from '@/features/common/components/Header';
import { JobDetailContent } from '@/features/common/components/JobDetailContent';
import MainLayout from '@/features/common/components/MainLayout';
import ConfirmApplyToJobDialog from '@/features/matching/components/ConfirmApplyToJobDialog';
import { useApplyToJob } from '@/features/matching/hooks/useApplyToJob';

interface Props {
  job: Job;
  cvId: string;
  onBack: () => void;
  onCancel: () => void;
  hasApplied: boolean;
  onApplicationSuccess: () => void;
}

export default function JobDetailsScreen({
  job,
  cvId,
  onBack,
  hasApplied,
  onApplicationSuccess,
}: Props) {
  const { t } = useTranslation();
  const { applyToJob, isApplying, confirmApplyOpen, setConfirmApplyOpen, applicationResult } =
    useApplyToJob({
      cvId,
      onSuccess: onApplicationSuccess,
    });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <MainLayout
        align="top-left"
        className="gap-4 sm:gap-8 pb-40"
        padding="p-4 sm:p-8"
        closeType={CloseTypeEnum.End}
        onClose={onBack}
        rightContent={
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              className="h-14 rounded-xl text-base font-bold px-5"
              onClick={onBack}
            >
              <ArrowLeftIcon className="mr-2 size-5" />
              {t('MatchingJobs.back_to_jobs')}
            </Button>
            <Button
              variant="default"
              className="h-14 rounded-xl text-base font-bold px-5"
              onClick={() => applyToJob(job)}
              disabled={isApplying || hasApplied}
            >
              {hasApplied ? t('dashboard.jobs.alreadyApplied') : t('MatchingJobs.apply')}
              {isApplying && <Loader2 className="mr-2 size-5 animate-spin" />}
            </Button>
          </div>
        }
      >
        <JobDetailContent
          job={job}
          showCard={true}
          isApplying={isApplying}
          hasApplied={hasApplied}
          handleApplyClick={() => applyToJob(job)}
        />
      </MainLayout>
      <ConfirmApplyToJobDialog
        open={confirmApplyOpen}
        onOpenChange={setConfirmApplyOpen}
        applicationResult={applicationResult}
      />
    </>
  );
}
